/**
 * Watchlist-parity advanced filtering for the pipeline TABLE view (SE-7144).
 *
 * The Table view's filter panel is the SAME panel (and the same committed
 * `PlayerAdvancedFilterInput` semantics) the add-players drawer ships —
 * which itself mirrors the Watchlist panel field-for-field. This module is
 * only the join: it lifts a board card's player (plus the SE-6871 metrics
 * sidecar row) into the `FilterablePoolPlayer` shape the shared predicate
 * evaluates, and narrows the table's columns to the players that pass.
 *
 * Server parity therefore comes from `matchesAdvancedFilters` — including
 * SQL-strict null handling and primary-OR-secondary role matching — and is
 * not re-derived here.
 * @module features/player-kanban/utils/tableAdvancedFilter
 */

import type { PlayerAdvancedFilterInput } from "@/generated/graphql";

import {
  matchesAdvancedFilters,
  matchesListSelection,
  type FilterablePoolPlayer,
} from "./addPlayersFilterPredicate";
import type { BoardPlayerMetrics } from "./playerFilter";
import type { ColumnData } from "../types/types";

/**
 * The raw board-card player fields the table filter reads — structural, so
 * the generated `ListKanbanCards` node satisfies it without casts.
 */
export interface TableFilterSourcePlayer {
  readonly id: string;
  readonly team?: { readonly id: string } | null;
  readonly league?: { readonly id: string } | null;
  readonly positions?: readonly ({ readonly id: string } | null)[] | null;
  readonly watchlists?: readonly ({ readonly id: string } | null)[] | null;
  readonly bioData?: {
    readonly age?: number | null;
    readonly gpr?: number | null;
    readonly nationality?: string | null;
    readonly contractExpires?: string | null;
    readonly roleArchetype?: string | null;
    readonly secondaryRoleArchetype?: string | null;
    readonly geminiPlayerValuation?: number | null;
    /** REPORT STATS source (SE-8608) — the card query already selects it. */
    readonly customStats?: unknown;
  } | null;
}

/**
 * Lift a board card's player into the shared filterable shape, joining the
 * PERFORMANCE / GOALKEEPER / FIT scores from the SE-6871 sidecar row (the
 * card query is complexity-capped and cannot carry them itself).
 * @param player - The raw card player.
 * @param metrics - The player's sidecar metrics row, if loaded.
 * @returns The player as the shared predicate consumes it.
 */
export const toFilterableBoardPlayer = (
  player: TableFilterSourcePlayer,
  metrics: BoardPlayerMetrics | undefined
): FilterablePoolPlayer => ({
  id: player.id,
  positions: player.positions,
  team: player.team,
  league: player.league,
  watchlists: player.watchlists,
  bioData: {
    age: player.bioData?.age,
    gpr: player.bioData?.gpr,
    nationality: player.bioData?.nationality,
    contractExpires: player.bioData?.contractExpires,
    roleArchetype: player.bioData?.roleArchetype,
    secondaryRoleArchetype: player.bioData?.secondaryRoleArchetype,
    geminiPlayerValuation: player.bioData?.geminiPlayerValuation,
    customStats: player.bioData?.customStats,
    fitScore: metrics?.fitScore,
    teamStyleFit: metrics?.teamStyleFit,
    carryingTransGlobalCategoricalScore: metrics?.carrying,
    dribblingTransGlobalCategoricalScore: metrics?.dribbling,
    passingTransGlobalCategoricalScore: metrics?.passing,
    shotLocationTransGlobalCategoricalScore: metrics?.shooting,
    gkPassingTransCategoricalScore: metrics?.gkPassing,
    gkShotStoppingTransCategoricalScore: metrics?.gkShotStopping,
    gkPositioningTransCategoricalScore: metrics?.gkPositioning,
  },
});

/** Arguments for {@link buildTableFilterPassSet}. */
export interface BuildTableFilterPassSetArgs {
  /** The raw card players on the active board. */
  readonly players: readonly TableFilterSourcePlayer[];
  /** SE-6871 sidecar metric rows by player id, when loaded. */
  readonly metricsById: ReadonlyMap<string, BoardPlayerMetrics> | undefined;
  /** The committed advanced input, as `buildAdvancedFilterInput` produced. */
  readonly advancedInput: PlayerAdvancedFilterInput;
  /** The committed LISTS selection (watchlist ids). */
  readonly listSelection: readonly string[];
  /** The panel's committed active-filter count — 0 means nothing narrows. */
  readonly activeFilterCount: number;
}

/**
 * The ids of the players that pass the committed panel state, or `null` when
 * nothing is active — the null lets callers keep referential stability by
 * skipping the narrowing entirely.
 * @param args - Players, metrics, and the committed panel state.
 * @returns Passing player ids, or `null` when no filter is active.
 */
export const buildTableFilterPassSet = (
  args: BuildTableFilterPassSetArgs
): ReadonlySet<string> | null => {
  const { players, metricsById, advancedInput, listSelection } = args;
  if (args.activeFilterCount === 0 && listSelection.length === 0) return null;
  const passing = players.filter(player => {
    const filterable = toFilterableBoardPlayer(
      player,
      metricsById?.get(player.id)
    );
    return (
      matchesAdvancedFilters(filterable, advancedInput) &&
      matchesListSelection(filterable, listSelection)
    );
  });
  return new Set(passing.map(player => player.id));
};

/**
 * Narrow every column to the passing players, keeping column identity and a
 * truthful `count` — the same structural contract
 * `applyBoardFiltersToColumns` holds. A `null` pass set returns the input BY
 * REFERENCE so an inactive panel costs nothing.
 * @param columns - The table's column data, or undefined while loading.
 * @param passSet - Passing player ids, or `null` when inactive.
 * @returns Columns narrowed to the passing players.
 */
export function applyTableFilterPassSet<T extends ColumnData[] | undefined>(
  columns: T,
  passSet: ReadonlySet<string> | null
): T {
  if (!columns || passSet === null) return columns;
  return columns.map(column => {
    const players = column.players.filter(player => passSet.has(player.id));
    return { ...column, players, count: players.length };
  }) as T;
}
